import React, { useEffect, useRef } from "react";
import { useRecoilValue } from "recoil";
import { patientState } from "../../recoil/atoms";
import { UserAuthClientResponse } from "../../recoil/types";

const TALKDESK_TOUCHPOINT_ID = "864daa0d537f455fa209685ee3fdd4bc";
const TALKDESK_REGION = "td-us-1";

const TALKDESK_SDK_URL =
  "https://talkdeskchatsdk.talkdeskapp.com/v2/talkdeskchatsdk.js";
const TALKDESK_SCRIPT_ID = "tdwebchatscript";
const TALKDESK_CONTAINER_ID = "tdWebchat";

declare global {
  interface Window {
    TalkdeskChatSDK?: (
      node: string,
      props: Record<string, unknown>,
    ) => TalkdeskWebchat;
  }
}

interface TalkdeskWebchat {
  init: (configs: Record<string, unknown>) => Promise<void>;
  destroy: () => void;
  setContextParam: (params: Record<string, string>) => void;
}

const WEBCHAT_CONFIGS = {
  enableEmoji: true,
  enableValidation: true,
  enableUserInput: true,
  enableAttachments: true,
  enableResponsiveLayout: true,
  enableOpenOnStart: false,
};

let activeInstanceId = 0;

function ensureContainer(): void {
  if (!document.getElementById(TALKDESK_CONTAINER_ID)) {
    const div = document.createElement("div");
    div.id = TALKDESK_CONTAINER_ID;
    document.body.appendChild(div);
  }
}

function removeContainer(): void {
  document.getElementById(TALKDESK_CONTAINER_ID)?.remove();
}

function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(
      TALKDESK_SCRIPT_ID,
    ) as HTMLScriptElement | null;

    if (existing) {
      if (window.TalkdeskChatSDK) {
        resolve();
        return;
      }
      const cleanup = () => {
        existing.removeEventListener("load", onLoad);
        existing.removeEventListener("error", onError);
      };
      const onLoad = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error("Failed to load Talkdesk SDK"));
      };
      existing.addEventListener("load", onLoad);
      existing.addEventListener("error", onError);
      // Re-check after attaching listeners to close the race window
      // where the script could finish loading between the SDK check above
      // and the addEventListener calls.
      if (window.TalkdeskChatSDK) {
        cleanup();
        resolve();
      }
      return;
    }

    const script = document.createElement("script");
    script.id = TALKDESK_SCRIPT_ID;
    script.src = TALKDESK_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error("Failed to load Talkdesk SDK"));
    };
    document.head.appendChild(script);
  });
}

function buildContextParams(
  patientInfo: UserAuthClientResponse | undefined,
): Record<string, string> {
  const context: Record<string, string> = {};
  if (!patientInfo) return context;

  if (patientInfo.user) {
    const {
      first_name: firstName,
      last_name: lastName,
      dob: dateOfBirth,
    } = patientInfo.user;
    if (firstName) context.contact_first_name = firstName;
    if (lastName) context.contact_last_name = lastName;
    if (dateOfBirth) context.contact_dob = dateOfBirth;
  }

  if (patientInfo.auth0?.email) {
    context.td_contact_person_identification = patientInfo.auth0.email;
  }

  return context;
}

export const TalkdeskChatWidget = (): React.ReactElement => {
  const webchatRef = useRef<TalkdeskWebchat | null>(null);
  const patientInfo = useRecoilValue(patientState);
  const patientInfoRef = useRef(patientInfo);
  patientInfoRef.current = patientInfo;

  useEffect(() => {
    const myInstanceId = ++activeInstanceId;
    let aborted = false;

    const safeDestroy = (instance: TalkdeskWebchat | null) => {
      try {
        instance?.destroy();
      } catch {
        /* expected — SDK may already be torn down */
      }
    };

    const destroyWebchat = () => {
      safeDestroy(webchatRef.current);
      webchatRef.current = null;
      if (myInstanceId === activeInstanceId) {
        removeContainer();
      }
    };

    const initWebchat = async () => {
      try {
        await loadScript();
      } catch (err) {
        console.error("[TalkdeskChatWidget]", err);
        return;
      }

      if (aborted || !window.TalkdeskChatSDK) return;

      destroyWebchat();
      ensureContainer();

      const webchat = window.TalkdeskChatSDK(TALKDESK_CONTAINER_ID, {
        touchpointId: TALKDESK_TOUCHPOINT_ID,
        accountId: "",
        region: TALKDESK_REGION,
      });

      const context = buildContextParams(patientInfoRef.current);
      if (Object.keys(context).length > 0) {
        webchat.setContextParam(context);
      }

      try {
        await webchat.init(WEBCHAT_CONFIGS);
      } catch (err) {
        console.error("[TalkdeskChatWidget] init failed", err);
        safeDestroy(webchat);
        return;
      }

      if (aborted) {
        safeDestroy(webchat);
        if (myInstanceId === activeInstanceId) {
          removeContainer();
        }
        return;
      }

      webchatRef.current = webchat;
    };

    initWebchat();

    return () => {
      aborted = true;
      destroyWebchat();
    };
  }, []);

  useEffect(() => {
    if (!webchatRef.current) return;
    const context = buildContextParams(patientInfo);
    if (Object.keys(context).length > 0) {
      webchatRef.current.setContextParam(context);
    }
  }, [patientInfo]);

  return <></>;
};
